import React, { useState, useEffect } from 'react';
import { memberService } from '../services/api'; // Import memberService
import './MembersTable.css';

const MembersTable = () => {
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [searchId, setSearchId] = useState('');
  const [editingMemberId, setEditingMemberId] = useState(null);
  const [editingMember, setEditingMember] = useState(null);
  const [quickViewMember, setQuickViewMember] = useState(null);
  const [showQuickViewModal, setShowQuickViewModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);

  // Load members from local storage on component mount
  useEffect(() => {
    const loadMembers = async () => {
      try {
        const membersList = await memberService.getAllMembers();
        setMembers(membersList);
        setFilteredMembers(membersList);
      } catch (error) {
        console.error('Error loading members:', error);
      }
    };
    loadMembers();
  }, []);

  // Filter members when searchId changes
  useEffect(() => {
    if (searchId.trim() === '') {
      setFilteredMembers(members);
    } else {
      const filtered = members.filter(member => 
        member.id.toString().toLowerCase().includes(searchId.toLowerCase())
      );
      setFilteredMembers(filtered);
    }
  }, [searchId, members]);

  const handleSearchChange = (e) => {
    setSearchId(e.target.value);
  };

  const clearSearch = () => {
    setSearchId('');
  };

  // Save members to local storage whenever they change
  const handleDeleteMember = (idToDelete) => {
    setMemberToDelete(idToDelete);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      await memberService.deleteMember(memberToDelete);
      setMembers(members.filter(member => member.id !== memberToDelete));
      setShowDeleteConfirm(false);
      setMemberToDelete(null);
    } catch (error) {
      console.error(`Error deleting member ${memberToDelete}:`, error);
      setShowDeleteConfirm(false);
      setMemberToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setMemberToDelete(null);
  };

  const handleEditMember = (memberToEdit) => {
    setEditingMemberId(memberToEdit.id);
    setEditingMember({ ...memberToEdit });
  };

  const handleSaveEdit = async () => {
    try {
      await memberService.updateMember(editingMemberId, editingMember);
      setMembers(members.map(member => 
        member.id === editingMemberId ? editingMember : member
      ));
      setEditingMemberId(null);
      setEditingMember(null);
    } catch (error) {
      console.error(`Error saving member ${editingMemberId}:`, error);
    }
  };

  const handleCancelEdit = () => {
    setEditingMemberId(null);
    setEditingMember(null);
  };

  const handleEditingMemberChange = (e) => {
    const { name, value } = e.target;
    setEditingMember(prev => ({ ...prev, [name]: value }));
  };

  const handleQuickView = (member) => {
    setQuickViewMember(member);
    setShowQuickViewModal(true);
  };

  const handleCloseQuickView = () => {
    setQuickViewMember(null);
    setShowQuickViewModal(false);
  };

  const handleViewTagForm = (memberId) => {
    window.open(`/tag/${memberId}`, '_blank');
  };

  return (
    <div className="members-table-container">
      <h2>Administrar Miembros</h2>
      
      {/* Buscador por ID */}
      <div className="search-container">
        <div className="search-input-group">
          <input
            type="text"
            placeholder="Buscar por ID..."
            value={searchId}
            onChange={handleSearchChange}
            className="search-input"
          />
          {searchId && (
            <button onClick={clearSearch} className="clear-search-button" title="Limpiar búsqueda">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
            </button>
          )}
        </div>
        {searchId && (
          <div className="search-results-info">
            Mostrando {filteredMembers.length} de {members.length} miembros
          </div>
        )}
      </div>

      {filteredMembers.length > 0 ? (
        <table className="members-table">
          <thead>
            <tr>
              <th>#</th>
              <th>ID</th>
              <th>Propietario</th>
              <th>Apellido</th>
              <th>Mascota</th>
              <th>Teléfono</th>
              <th>Dirección</th>
              <th>Email</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filteredMembers.map((member, index) => (
              <tr key={member.id}>
                <td>{index + 1}</td>
                <td>{member.id}</td>
                <td>
                  {editingMemberId === member.id ? (
                    <input type="text" name="nombrePropietario" value={editingMember?.nombrePropietario || ''} onChange={handleEditingMemberChange} />
                  ) : (
                    member.nombrePropietario
                  )}
                </td>
                <td>
                  {editingMemberId === member.id ? (
                    <input type="text" name="apellidoPropietario" value={editingMember?.apellidoPropietario || ''} onChange={handleEditingMemberChange} />
                  ) : (
                    member.apellidoPropietario
                  )}
                </td>
                <td>
                  {editingMemberId === member.id ? (
                    <input type="text" name="nombreMascota" value={editingMember?.nombreMascota || ''} onChange={handleEditingMemberChange} />
                  ) : (
                    member.nombreMascota
                  )}
                </td>
                <td>
                  {editingMemberId === member.id ? (
                    <input type="text" name="telefono" value={editingMember?.telefono || ''} onChange={handleEditingMemberChange} />
                  ) : (
                    member.telefono
                  )}
                </td>
                <td>
                  {editingMemberId === member.id ? (
                    <input type="text" name="direccion" value={editingMember?.direccion || ''} onChange={handleEditingMemberChange} />
                  ) : (
                    member.direccion
                  )}
                </td>
                <td>
                  {editingMemberId === member.id ? (
                    <input type="email" name="email" value={editingMember?.email || ''} onChange={handleEditingMemberChange} />
                  ) : (
                    member.email
                  )}
                </td>
                <td className="actions-cell">
                  {editingMemberId === member.id ? (
                    <>
                      <button onClick={handleSaveEdit} className="save-button" title="Guardar">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                        </svg>
                      </button>
                      <button onClick={handleCancelEdit} className="cancel-button" title="Cancelar">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                        </svg>
                      </button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => handleViewTagForm(member.id)} className="form-button" title="Ver formulario completo">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
                        </svg>
                      </button>
                      <button onClick={() => handleQuickView(member)} className="view-button" title="Ver detalles">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                        </svg>
                      </button>
                      <button onClick={() => handleEditMember(member)} className="edit-button" title="Editar">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                        </svg>
                      </button>
                      <button onClick={() => handleDeleteMember(member.id)} className="delete-button" title="Eliminar">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                        </svg>
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="no-results">
          {searchId ? (
            <p>No se encontraron miembros con el ID "{searchId}"</p>
          ) : (
            <p>No hay miembros registrados.</p>
          )}
        </div>
      )}

      {showQuickViewModal && quickViewMember && (
        <div className="quick-view-modal-overlay">
          <div className="quick-view-modal-content">
            <h3>Detalles del Miembro</h3>
            <p><strong>ID:</strong> {quickViewMember.id}</p>
            <p><strong>Nombre Propietario:</strong> {quickViewMember.nombrePropietario}</p>
            <p><strong>Apellido Propietario:</strong> {quickViewMember.apellidoPropietario}</p>
            <p><strong>Nombre Mascota:</strong> {quickViewMember.nombreMascota}</p>
            <p><strong>Teléfono:</strong> {quickViewMember.telefono}</p>
            <p><strong>Dirección:</strong> {quickViewMember.direccion}</p>
            <p><strong>Email:</strong> {quickViewMember.email}</p>
            <button onClick={handleCloseQuickView} className="close-modal-button">Cerrar</button>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="quick-view-modal-overlay">
          <div className="quick-view-modal-content">
            <h3>Confirmar Eliminación</h3>
            <p style={{textAlign: 'center', fontSize: '1.1em', marginBottom: '10px'}}>
              ¿Está seguro que desea eliminar este usuario?
            </p>
            <div style={{display: 'flex', gap: '10px', justifyContent: 'center'}}>
              <button onClick={confirmDelete} className="close-modal-button" style={{padding: '10px 20px', minWidth: '100px'}}>
                Eliminar
              </button>
              <button onClick={cancelDelete} className="close-modal-button" style={{padding: '10px 20px', minWidth: '100px'}}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MembersTable;
